import React, { useState } from "react";
import { Trash2, Edit } from "lucide-react";
import { subcategoryService } from "../../services/subcategoryService";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DeleteConfirmation from "../common/DeleteConfirmation";
import SubcategoryForm from "./SubcategoryForm";

const SubcategoryList = ({
  subcategories,
  categories,
  viewMode,
  setSubcategories,
  selectedCategory,
}) => {
  const [loading, setLoading] = useState(false);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Ensure subcategories is an array and filter
  const filteredSubcategories = selectedCategory
    ? (subcategories?.results || subcategories || []).filter(
        (sub) => sub.categoryId === selectedCategory.id
      )
    : subcategories?.results || subcategories || [];

  const handleDeleteSubcategory = async () => {
    try {
      setLoading(true);
      await subcategoryService.deleteSubcategory(selectedSubcategory._id);
      setSubcategories((prev) =>
        (prev.results ? prev.results : prev).filter(
          (sub) => sub._id !== selectedSubcategory._id
        )
      );

      const updatedSubcategories = await subcategoryService.getSubcategories();
      setSubcategories(updatedSubcategories);

      toast.success("Subcategory deleted successfully!");
      setIsDeleteModalOpen(false);
    } catch (error) {
      console.error("Failed to delete subcategory:", error);
      toast.error("Failed to delete subcategory!");
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubcategory = async (formData) => {
    try {
      setLoading(true);
      const updatedSubcategory = await subcategoryService.updateSubcategory(
        selectedSubcategory._id,
        formData
      );
      setSubcategories((prev) =>
        (prev.results || prev).map((cat) =>
          cat._id === updatedSubcategory._id ? updatedSubcategory : cat
        )
      );
      const updatedSubcategories = await subcategoryService.getSubcategories();
      setSubcategories(updatedSubcategories);

      setIsEditModalOpen(false);
      toast.success("Subcategory updated successfully!");
    } catch (error) {
      console.error("Failed to update subcategory:", error);
      toast.error("Failed to update subcategory!");
    } finally {
      setLoading(false);
    }
  };

  const renderGridView = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center h-40">
          <div className="spinner border-t-4 border-blue-500 w-8 h-8 rounded-full animate-spin"></div>
        </div>
      );
    }
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {filteredSubcategories.map((subcategory, index) => (
          <div
            key={subcategory._id || index}
            className="bg-white shadow rounded-lg p-4 flex flex-col items-start"
          >
            <img
              src={subcategory.image}
              alt={subcategory.name || "Subcategory"}
              className="w-full h-32 object-cover rounded-md mb-4"
            />
            <h3 className="text-lg font-bold mb-2">{subcategory.name}</h3>
            <p className="text-gray-600 text-sm mb-2">
              Category: {subcategory.category?.name || "Unassigned"}
            </p>
            {subcategory.category?.promotion?.discounts?.[0] && (
              <div className="text-sm text-green-500 bg-green-100 px-2 py-1 rounded-md mb-2">
                Promotion: Buy{" "}
                {subcategory.category.promotion.discounts[0].buyX}, next gets{" "}
                {subcategory.category.promotion.discounts[0].discountOnNext}%
                off
              </div>
            )}
            <div className="flex space-x-2 mt-auto">
              <button
                onClick={() => {
                  setSelectedSubcategory(subcategory);
                  setIsEditModalOpen(true);
                }}
                className="text-blue-500 hover:text-blue-700"
              >
                <Edit size={18} />
              </button>
              <button
                onClick={() => {
                  setSelectedSubcategory(subcategory);
                  setIsDeleteModalOpen(true);
                }}
                className="text-red-500 hover:text-red-700"
              >
                <Trash2 size={18} />
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderListView = () => (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      {filteredSubcategories.length === 0 && (
        <p className="text-center text-gray-500">No subcategories found.</p>
      )}
      {filteredSubcategories.map((subcategory, index) => (
        <div
          key={subcategory._id || index}
          className="flex justify-between items-center p-4 border-b last:border-b-0 hover:bg-gray-50"
        >
          <div className="flex items-center space-x-4">
            <img
              src={subcategory.image}
              alt={subcategory.name}
              className="w-12 h-12 object-cover rounded-md"
            />
            <div>
              <h3 className="text-lg font-bold">{subcategory.name}</h3>
              <p className="text-sm text-gray-600">
                Category: {subcategory.category?.name || "Unassigned"}
              </p>
            </div>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => {
                setSelectedSubcategory(subcategory);
                setIsEditModalOpen(true);
              }}
              className="text-blue-500 hover:text-blue-700"
            >
              <Edit size={18} />
            </button>
            <button
              disabled={loading}
              className={`text-red-500 hover:text-red-700 ${
                loading && "opacity-50"
              }`}
            >
              <Trash2 size={18} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <>
      {viewMode === "grid" ? renderGridView() : renderListView()}

      <DeleteConfirmation
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteSubcategory}
        itemName="subcategory"
      />

      {selectedSubcategory && (
        <SubcategoryForm
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSubmit={handleEditSubcategory}
          initialData={selectedSubcategory}
          categories={categories}
        />
      )}
    </>
  );
};

export default SubcategoryList;
